import React, { useEffect, useState, useRef } from 'react';

import classnames from 'classnames';

import { useSize } from '../../../features/config';
import useLineStyles from '../hooks/useLineStyles';
import useUniqueTabIds from '../hooks/useUniqueTabIds';
import { type TabsLinesProps } from '../Tabs';

import styles from './TabsLines.module.scss';

export const TabsLines = ({
    items,
    activeKey,
    defaultActiveKey,
    onChange,
    orientation = 'horizontal',
    tabAlignment = 'left',
    hideDivider = false,
    tabsFullWidth = false,
    hideTabBar = false,
    fullHeight = false,
    destroyInactiveTabPane = false,
    tabBarExtraContent,
    disableTabPanelPadding = false,
    size: sizeProp,
    tabBarStyle,
    className
}: Omit<TabsLinesProps, 'type'>) => {
    const isControlled = activeKey !== undefined;
    const lazyLoadedKeys = useRef(new Set());
    const size = useSize(sizeProp, 'medium', 'Tabs');

    const [internalActiveKey, setInternalActiveKey] = useState(() => {
        if (isControlled) {
            return activeKey;
        }
        return defaultActiveKey || (items.length > 0 ? items[0].key : '');
    });

    useEffect(() => {
        if (isControlled) {
            setInternalActiveKey(activeKey);
        }
    }, [activeKey]);

    const currentActiveKey = isControlled ? activeKey : internalActiveKey;

    const { lineStyles, register } = useLineStyles(currentActiveKey, orientation);

    lazyLoadedKeys.current.add(currentActiveKey);

    const handleTabChange = (key: string) => {
        if (!isControlled) {
            setInternalActiveKey(key);
        }

        if (onChange) {
            onChange(key);
        }
    };

    const { getTabId, getPanelId } = useUniqueTabIds();

    return (
        <div
            className={classnames(
                styles.tabs,
                {
                    [styles.vertical]: orientation === 'vertical',
                    [styles.fullHeight]: fullHeight
                },
                className
            )}
        >
            <div
                className={classnames(styles.header, {
                    [styles.divider]: !hideDivider,
                    [styles.hideTabBar]: hideTabBar
                })}
                style={tabBarStyle}
            >
                {tabBarExtraContent?.left}
                <div
                    role="tablist"
                    aria-orientation={orientation}
                    className={classnames(styles.tablist, {
                        [styles.alignLeft]: tabAlignment === 'left',
                        [styles.alignCenter]: tabAlignment === 'center',
                        [styles.alignRight]: tabAlignment === 'right'
                    })}
                >
                    <div
                        className={classnames(styles.lineWrapper, {
                            [styles.fullWidth]: tabsFullWidth
                        })}
                    >
                        {items.map(({ key, label, disabled, id }) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={currentActiveKey === key}
                                aria-controls={getPanelId(key)}
                                id={id || getTabId(key)}
                                disabled={disabled}
                                className={classnames(styles.tab, {
                                    [styles.active]: currentActiveKey === key,
                                    [styles.sizeSmall]: size === 'small',
                                    [styles.sizeLarge]: size === 'large',
                                    [styles.horizontal]: orientation === 'horizontal'
                                })}
                                onClick={() => handleTabChange(key)}
                                ref={register(key)}
                            >
                                {label}
                            </button>
                        ))}
                        <div className={styles.line} style={lineStyles} />
                    </div>
                </div>
                {tabBarExtraContent?.right}
            </div>

            {items.map(({ key, children, className: panelClassName, disablePadding, id }) => {
                if (!children) {
                    return null;
                }

                const shouldShowChildren = destroyInactiveTabPane
                    ? currentActiveKey === key
                    : lazyLoadedKeys.current.has(key);

                return (
                    <div
                        key={key}
                        role="tabpanel"
                        hidden={currentActiveKey !== key}
                        aria-labelledby={id || getTabId(key)}
                        id={getPanelId(key)}
                        className={classnames(
                            styles.tabpanel,
                            {
                                [styles.disablePadding]: disableTabPanelPadding || disablePadding
                            },
                            panelClassName
                        )}
                    >
                        {shouldShowChildren && children}
                    </div>
                );
            })}
        </div>
    );
};

export default TabsLines;
